import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheck, FiChevronRight } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';

const steps = ['Shipping', 'Payment', 'Review'];

const Checkout = () => {
  const [step, setStep] = useState(0);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const { state, dispatch, totalPrice } = useCart();
  const { addToast } = useToast();

  const [shipping, setShipping] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', zip: '', country: 'US',
  });

  const [payment, setPayment] = useState({
    cardName: '', cardNumber: '', expiry: '', cvv: '', method: 'card',
  });

  const handleShippingChange = e => setShipping({ ...shipping, [e.target.name]: e.target.value });
  const handlePaymentChange = e => setPayment({ ...payment, [e.target.name]: e.target.value });

  const handleNext = () => {
    if (step < steps.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handlePlaceOrder = () => {
    dispatch({ type: 'CLEAR_CART' });
    setOrderPlaced(true);
    addToast('🎉 Order placed successfully!');
  };

  const shippingFee = totalPrice > 9900 ? 0 : 990;
  const tax = totalPrice * 0.08;
  const total = totalPrice + shippingFee + tax;

  if (orderPlaced) {
    return (
      <div className="checkout-success">
        <div className="container">
          <motion.div
            className="success-content"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
          >
            <motion.div
              className="success-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <FiCheck size={48} />
            </motion.div>
            <h1>Order Placed! 🎉</h1>
            <p>Thank you for shopping with us. Your order is being processed.</p>
            <p className="order-id">Order #LUX{Date.now().toString().slice(-6)}</p>
            <div className="success-actions">
              <Link to="/" className="btn btn-primary btn-lg">Back to Home</Link>
              <Link to="/shop" className="btn btn-outline btn-lg">Continue Shopping</Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="page-title">Checkout</h1>

        {/* Progress Steps */}
        <div className="checkout-steps">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`step ${i <= step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                <div className="step-circle">
                  {i < step ? <FiCheck size={14} /> : i + 1}
                </div>
                <span className="step-label">{s}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`step-line ${i < step ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="checkout-layout">
          {/* Form */}
          <div className="checkout-form">
            {/* Step 0: Shipping */}
            {step === 0 && (
              <motion.div
                className="form-section"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h2>Shipping Information</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name</label>
                    <input name="firstName" value={shipping.firstName} onChange={handleShippingChange} placeholder="John" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input name="lastName" value={shipping.lastName} onChange={handleShippingChange} placeholder="Doe" className="form-input" />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Email</label>
                    <input name="email" type="email" value={shipping.email} onChange={handleShippingChange} placeholder="john@example.com" className="form-input" />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Phone</label>
                    <input name="phone" value={shipping.phone} onChange={handleShippingChange} placeholder="+1 (555) 000-0000" className="form-input" />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Address</label>
                    <input name="address" value={shipping.address} onChange={handleShippingChange} placeholder="123 Main St" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input name="city" value={shipping.city} onChange={handleShippingChange} placeholder="New York" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code</label>
                    <input name="zip" value={shipping.zip} onChange={handleShippingChange} placeholder="10001" className="form-input" />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Country</label>
                    <select name="country" value={shipping.country} onChange={handleShippingChange} className="form-input">
                      <option value="JP">Japan</option>
                     
                    </select>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 1: Payment */}
            {step === 1 && (
              <motion.div
                className="form-section"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h2>Payment Details</h2>
                <div className="payment-methods-tabs">
                  {[
                    { value: 'card', label: '💳 Credit Card' },
                    { value: 'paypal', label: 'PayPal' },
                    { value: 'apple', label: '🍎 Apple Pay' },
                    { value: 'COD', label: '💵 Cash on Delivery' },
                  ].map(m => (
                    <button
                      key={m.value}
                      className={`payment-tab ${payment.method === m.value ? 'active' : ''}`}
                      onClick={() => setPayment({ ...payment, method: m.value })}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {payment.method === 'card' && (
                  <div className="form-grid">
                    <div className="form-group form-group-full">
                      <label>Cardholder Name</label>
                      <input name="cardName" value={payment.cardName} onChange={handlePaymentChange} placeholder="John Doe" className="form-input" />
                    </div>
                    <div className="form-group form-group-full">
                      <label>Card Number</label>
                      <input name="cardNumber" value={payment.cardNumber} onChange={handlePaymentChange} placeholder="4242 4242 4242 4242" className="form-input" maxLength="19" />
                    </div>
                    <div className="form-group">
                      <label>Expiry Date</label>
                      <input name="expiry" value={payment.expiry} onChange={handlePaymentChange} placeholder="MM/YY" className="form-input" maxLength="5" />
                    </div>
                    <div className="form-group">
                      <label>CVV</label>
                      <input name="cvv" value={payment.cvv} onChange={handlePaymentChange} placeholder="123" className="form-input" maxLength="3" type="password" />
                    </div>
                  </div>
                )}

                {payment.method === 'paypal' && (
                  <div className="alt-payment-info">
                    <p>You'll be redirected to PayPal to complete your payment securely.</p>
                  </div>
                )}

                {payment.method === 'apple' && (
                  <div className="alt-payment-info">
                    <p>Complete your purchase using Apple Pay. Touch ID or Face ID required.</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2: Review */}
            {step === 2 && (
              <motion.div
                className="form-section"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h2>Review Your Order</h2>
                <div className="review-items">
                  {state.items.map(item => (
                    <div key={item.id} className="review-item">
                      <img src={item.image} alt={item.name} className="review-item-img" />
                      <div>
                        <p className="review-item-name">{item.name}</p>
                        <p className="review-item-qty">Qty: {item.quantity}</p>
                      </div>
                      <span className="review-item-price">¥{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="review-shipping-info">
                  <h4>Shipping to:</h4>
                  <p>{shipping.firstName} {shipping.lastName}</p>
                  <p>{shipping.address}, {shipping.city} {shipping.zip}</p>
                  <p>{shipping.email}</p>
                </div>
              </motion.div>
            )}

            {/* Navigation */}
            <div className="form-nav">
              {step > 0 && (
                <button className="btn btn-ghost" onClick={handleBack}>Back</button>
              )}
              {step < steps.length - 1 ? (
                <button className="btn btn-primary" onClick={handleNext}>
                  Continue <FiChevronRight />
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handlePlaceOrder}>
                  Place Order 🎉
                </button>
              )}
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="checkout-summary">
            <h3>Order Summary</h3>
            <div className="checkout-items">
              {state.items.map(item => (
                <div key={item.id} className="checkout-item">
                  <img src={item.image} alt={item.name} className="checkout-item-img" />
                  <div>
                    <p>{item.name}</p>
                    <p className="checkout-item-qty">×{item.quantity}</p>
                  </div>
                  <span>¥{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="checkout-totals">
              <div className="summary-line">
                <span>Subtotal</span><span>¥{totalPrice.toFixed(2)}</span>
              </div>
              <div className="summary-line">
                <span>Shipping</span>
                <span>{shippingFee === 0 ? 'FREE' : `¥${shippingFee}`}</span>
              </div>
              <div className="summary-line">
                <span>Tax</span><span>¥{tax.toFixed(2)}</span>
              </div>
              <div className="summary-divider" />
              <div className="summary-line summary-total-line">
                <span>Total</span><span>¥{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
